import { Cookie } from 'core-events/components';
import { RecaptchaV3Action } from 'google-recaptcha/components';
import { useFormWithRecaptchaV3 } from 'google-recaptcha/components';
import { unstable_rethrow } from 'next/navigation';
import { FormEvent, useCallback } from 'react';

import { LoggerFactory } from '@videoblocks/typescript-logger';

import { getCookie } from '../../../components/lib/cookies/getCookie';
import { validatePassword } from '../../../lib/validation/passwordValidation';
import { useSignUpContext } from './SignUpContext';
import { signUpAction } from './signUpAction';

const logger = LoggerFactory.create('useSubmitSignUpForm');

export function useSubmitSignUpForm(
    planCode?: string,
    redirectAfterRegister?: string,
): (event: FormEvent<HTMLFormElement>) => void {
    const { setError, setSubmitText } = useSignUpContext();

    const handleFormSubmit = useCallback(
        async (formData: FormData, recaptchaToken?: string) => {
            const email = formData.get('email') as string;
            const password = formData.get('password') as string;
            const uniqueId = getCookie(Cookie.Vid);

            // Basic email validation
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(email)) {
                logger.error('Invalid email format');
                setError('Please enter a valid email address.');
                return;
            }

            // Password validation
            const validation = validatePassword(password);
            if (!validation.isValid) {
                logger.error(validation.errors[0]);
                setError(validation.errors[0]);
                return;
            }

            setSubmitText('Processing...');

            try {
                const result = await signUpAction({
                    redirectAfterRegister,
                    uniqueId,
                    email,
                    password,
                    planCode,
                    signupType: 'email_and_password',
                    recaptchaToken,
                });

                if (result && result?.error) {
                    setError(result.error);
                    setSubmitText('Create Account');
                }
            } catch (e) {
                // Let Next.js control-flow errors (e.g. redirect from signUpAction) bubble up to the router.
                unstable_rethrow(e);
                logger.error(e);
                setError('An unexpected error occurred. Please try again.');
                setSubmitText('Create Account');
            }
        },
        [setError, setSubmitText, planCode, redirectAfterRegister],
    );

    const handleError = useCallback(() => {
        setError('Oops! Something went wrong. Please try again.');
        setSubmitText('Create Account');
    }, [setError, setSubmitText]);

    return useFormWithRecaptchaV3({
        action: RecaptchaV3Action.SIGN_UP_WITH_CREDENTIALS,
        onSubmit: handleFormSubmit,
        onError: handleError,
    });
}
